import { CardContent } from "@mui/material";
import Card from "@mui/material/Card";
import type { CardProps } from "@mui/material/Card";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import Avatar from "../components/avatar";

type StatusOverlayProps = {
  cardVariant?: CardProps["variant"];
  header?: ReactNode;
  title: ReactNode;
  children: ReactNode;
};

/**
 * Fullscreen blocking overlay that centers a Card.
 * Used for "absence", "volume limit exceeded" and similar mailbox states.
 */
const StatusOverlay = ({
  cardVariant = "elevation",
  header,
  title,
  children,
}: StatusOverlayProps) => {
  return (
    <div className="fixed inset-0 z-50 h-screen w-screen overflow-hidden backdrop-blur-[8px]">
      <div className="flex h-screen w-screen flex-col items-center justify-center px-4 sm:px-14">
        <Card variant={cardVariant} className="select-none">
          <CardContent>
            <div className="w-full max-w-[280px] px-[10px] pb-[6px] pt-5 will-change-[max-width,padding] motion-safe:transition-[max-width,padding] motion-safe:duration-300 motion-safe:ease-[cubic-bezier(.2,.8,.2,1)] sm:max-w-[670px] sm:px-[56px] sm:pb-[43px]">
              {header}
              {header && <div className="h-6 sm:h-3" />}
              {title}
              <div className="h-4" />
              {children}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

type AbsenceOverlayProp = {
  uid: string;
  username?: string;
  email: string;
  absenceMessage: string | null;
};

/**
 * Absence overlay
 */
export const AbsenceModeOverlay = ({
  uid,
  username,
  email,
  absenceMessage,
}: AbsenceOverlayProp) => {
  const { t } = useTranslation();

  return (
    <StatusOverlay
      cardVariant="absentUser"
      header={<UserDetails uid={uid} username={username} email={email} />}
      title={<h4>{t("Temporarily absent")}</h4>}
    >
      <p className="whitespace-pre-wrap break-words font-extralight">
        {absenceMessage ??
          t(
            "This mailbox is unavailable during my absence. You may try again once I have returned."
          )}
      </p>
    </StatusOverlay>
  );
};

type VolumeLimitOverlayProps = {
  uid: string;
  username?: string;
  email: string;
};

/**
 * Visible when mailbox.volumeLimitExceeded === true.
 * Same layout as absence overlay, different copy.
 */
export const VolumeLimitExceededOverlay = ({
  uid,
  username,
  email,
}: VolumeLimitOverlayProps) => {
  const { t } = useTranslation();

  return (
    <StatusOverlay
      cardVariant="absentUser"
      title={
        <h4 className="mt-3 text-center font-medium">
          {t("The mailbox storage limit has been reached")}
        </h4>
      }
    >
      <p className="whitespace-pre-wrap break-words font-light">
        {t(
          "The recipient's mailbox has reached its storage limit. No further data can be sent at the moment."
        )}
      </p>
    </StatusOverlay>
  );
};

type UserDetailsProp = {
  uid: string;
  username?: string;
  email: string;
};

/**
 * Shared user header used by both overlays.
 */
const UserDetails = ({ uid, username, email }: UserDetailsProp) => {
  return (
    <div className="pointer-events-none flex flex-col items-center justify-center">
      <Avatar
        uid={uid}
        name={username || email}
        size={70}
        isAnonymous={false}
        outline="ring"
      />
      <div className="h-2" />
      <h3 className="font-medium">{username || email}</h3>
    </div>
  );
};
